/**
 * SignaturePad — firma digital robusta para web + mobile (Android/iOS).
 *
 * Mejoras vs. wrapper trivial de `react-signature-canvas`:
 *  1. Escalado por `devicePixelRatio` para trazos nítidos en alta densidad.
 *  2. `ResizeObserver` + reescalado del canvas cuando el contenedor cambia
 *     (rotación, aparición del teclado, modal que crece).
 *  3. `touch-action: none` + `overscroll-behavior: contain` + bloqueo de
 *     pointer events en el padre durante la firma (evita que el dedo
 *     dispare scroll del modal mientras se firma).
 *  4. Botón "Pantalla completa" → overlay fullscreen con canvas alto y
 *     orientación libre, ideal para móvil. Dentro del overlay el `body`
 *     se bloquea (`overflow:hidden`) para no scrollear de fondo.
 *  5. Detección de "trazo vacío" (mínimo de 5 puntos) para evitar
 *     confirmar firmas accidentales tipo tap.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import SignatureCanvas from "react-signature-canvas";
import Button from "@/components/ui/Button";
import { Maximize2, X } from "lucide-react";
import { cn } from "@/lib/utils";

export interface SignaturePadProps {
  label?: string;
  hint?: string;
  disabled?: boolean;
  onConfirm: (pngDataUrl: string) => void;
  onClear?: () => void;
  initialDataUrl?: string;
  className?: string;
}

interface InnerApi {
  getDataUrl: () => string | null;
  isEmpty: () => boolean;
  clear: () => void;
}

interface InnerCanvasProps {
  minHeight: number;
  disabled?: boolean;
  onReady: (api: InnerApi) => void;
}

function InnerCanvas({ minHeight, disabled, onReady }: InnerCanvasProps) {
  const sigRef = useRef<SignatureCanvas | null>(null);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const persistedDataRef = useRef<unknown[] | null>(null);

  const resize = useCallback(() => {
    const wrap = wrapRef.current;
    const sig = sigRef.current;
    if (!wrap || !sig) return;

    const rect = wrap.getBoundingClientRect();
    const cssW = Math.max(200, Math.floor(rect.width));
    const cssH = Math.max(minHeight, Math.floor(rect.height));
    const dpr = Math.min(3, Math.max(1, window.devicePixelRatio || 1));

    const canvas = sig.getCanvas();
    const needs = canvas.width !== cssW * dpr || canvas.height !== cssH * dpr;
    if (!needs) return;

    if (!sig.isEmpty()) {
      try {
        persistedDataRef.current = sig.toData();
      } catch {
        persistedDataRef.current = null;
      }
    }

    canvas.width = cssW * dpr;
    canvas.height = cssH * dpr;
    canvas.style.width = `${cssW}px`;
    canvas.style.height = `${cssH}px`;
    const ctx = canvas.getContext("2d");
    if (ctx) ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    if (persistedDataRef.current) {
      try {
        sig.fromData(
          persistedDataRef.current as Parameters<typeof sig.fromData>[0],
        );
      } catch {
        // ignore
      }
    }
  }, [minHeight]);

  useEffect(() => {
    resize();
    if (!wrapRef.current) return;
    const ro = new ResizeObserver(() => resize());
    ro.observe(wrapRef.current);
    window.addEventListener("orientationchange", resize);
    return () => {
      ro.disconnect();
      window.removeEventListener("orientationchange", resize);
    };
  }, [resize]);

  useEffect(() => {
    onReady({
      getDataUrl: () => {
        const sig = sigRef.current;
        if (!sig || sig.isEmpty()) return null;
        try {
          return sig.getTrimmedCanvas().toDataURL("image/png");
        } catch {
          return sig.getCanvas().toDataURL("image/png");
        }
      },
      isEmpty: () => {
        const sig = sigRef.current;
        if (!sig) return true;
        // Confiamos en el detector interno de la lib. Cualquier conteo
        // adicional de puntos genera falsos negativos en trazos cortos.
        return sig.isEmpty();
      },
      clear: () => {
        sigRef.current?.clear();
        persistedDataRef.current = null;
      },
    });
  }, [onReady]);

  return (
    <div
      ref={wrapRef}
      className="w-full h-full bg-white rounded-lg"
      style={{
        minHeight,
        touchAction: "none",
        overscrollBehavior: "contain",
        WebkitUserSelect: "none",
        userSelect: "none",
      }}
      onPointerDown={(e) => e.stopPropagation()}
      onTouchStart={(e) => e.stopPropagation()}
      onTouchMove={(e) => e.stopPropagation()}
    >
      <SignatureCanvas
        ref={sigRef}
        penColor="#111827"
        backgroundColor="rgba(255,255,255,1)"
        minWidth={1.2}
        maxWidth={2.4}
        velocityFilterWeight={0.6}
        clearOnResize={false}
        canvasProps={{
          className: "w-full h-full rounded-lg",
          style: {
            touchAction: "none",
            display: "block",
            cursor: disabled ? "not-allowed" : "crosshair",
          },
        }}
      />
    </div>
  );
}

export function SignaturePad({
  label = "Firma",
  hint = "Firme con el dedo o el mouse dentro del recuadro",
  disabled,
  onConfirm,
  onClear,
  initialDataUrl,
  className,
}: SignaturePadProps) {
  const [confirmed, setConfirmed] = useState<string | null>(
    initialDataUrl ?? null,
  );
  const [error, setError] = useState<string | null>(null);
  const [fullscreen, setFullscreen] = useState(false);

  const apiRef = useRef<InnerApi | null>(null);

  const handleApiReady = useCallback((api: InnerApi) => {
    apiRef.current = api;
  }, []);

  const handleClear = () => {
    apiRef.current?.clear();
    setConfirmed(null);
    setError(null);
    onClear?.();
  };

  const handleConfirm = () => {
    const api = apiRef.current;
    if (!api || api.isEmpty()) {
      setError("Firme antes de confirmar.");
      return;
    }
    const dataUrl = api.getDataUrl();
    if (!dataUrl) {
      setError("No se pudo capturar la firma. Intente de nuevo.");
      return;
    }
    setConfirmed(dataUrl);
    setError(null);
    setFullscreen(false);
    onConfirm(dataUrl);
  };

  useEffect(() => {
    if (!fullscreen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [fullscreen]);

  return (
    <div className={cn("w-full", className)}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label}
        </label>
      )}

      {confirmed ? (
        <div className="border-2 border-green-300 rounded-lg p-2 bg-green-50">
          <img
            src={confirmed}
            alt="Firma confirmada"
            className="max-h-40 mx-auto"
          />
          <div className="mt-2 flex justify-end">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleClear}
              disabled={disabled}
            >
              Rehacer firma
            </Button>
          </div>
        </div>
      ) : (
        <>
          <div className="border-2 border-gray-300 rounded-lg bg-white">
            <InnerCanvas
              minHeight={220}
              disabled={disabled}
              onReady={handleApiReady}
            />
          </div>
          {hint && <p className="text-xs text-gray-500 mt-1">{hint}</p>}
          {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
          <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setFullscreen(true)}
              disabled={disabled}
              className="flex items-center gap-1"
            >
              <Maximize2 className="w-4 h-4" />
              Pantalla completa
            </Button>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleClear}
                disabled={disabled}
              >
                Limpiar
              </Button>
              <Button
                type="button"
                variant="primary"
                size="sm"
                onClick={handleConfirm}
                disabled={disabled}
              >
                Confirmar firma
              </Button>
            </div>
          </div>
        </>
      )}

      {fullscreen && (
        <div
          className="fixed inset-0 z-[60] bg-gray-900/95 flex flex-col"
          style={{ height: "100dvh" }}
        >
          <div className="flex items-center justify-between px-4 py-3 text-white">
            <div>
              <p className="font-semibold text-sm">{label}</p>
              <p className="text-xs text-white/60">
                Gire el dispositivo si necesita más espacio.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setFullscreen(false)}
              className="w-9 h-9 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20"
              aria-label="Cerrar"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="flex-1 px-3 pb-2 min-h-0">
            <div className="w-full h-full bg-white rounded-xl overflow-hidden border-2 border-white/30">
              <InnerCanvas
                minHeight={300}
                disabled={disabled}
                onReady={handleApiReady}
              />
            </div>
          </div>

          {error && <p className="px-4 pb-1 text-xs text-red-300">{error}</p>}

          <div className="flex gap-2 px-4 pb-5 pt-2">
            <Button
              type="button"
              variant="outline"
              className="flex-1 bg-white/10 border-white/20 text-white hover:bg-white/20 hover:text-white"
              onClick={handleClear}
              disabled={disabled}
            >
              Limpiar
            </Button>
            <Button
              type="button"
              variant="primary"
              className="flex-1"
              onClick={handleConfirm}
              disabled={disabled}
            >
              Confirmar firma
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default SignaturePad;
